import { useMutation } from '@apollo/client';
import {
  AddCommentInput,
  Case,
  MutationResponse,
  TransactionErrorDisplay,
} from '@bybeam/platform-types';
import Button from '@platform-ui-common/components/Button/index.js';
import ErrorDisplay from '@platform-ui-common/components/ErrorDisplay/index.js';
import TextInput from '@platform-ui-common/components/TextInput/index.js';
import { fieldUpdate } from '@platform-ui-common/hooks/useForm/actions.js';
import {
  characterCountValidation,
  requiredField,
} from '@platform-ui-common/hooks/useForm/fields.js';
import useForm from '@platform-ui-common/hooks/useForm/hook.js';
import { ValidationMode } from '@platform-ui-common/hooks/useForm/types.js';
import useSnackbar from '@platform-ui-common/hooks/useSnackbar/index.js';
import Color from '@platform-ui-common/utilities/Color.js';
import AddParticipantComment from '@platform-ui/application/graphql/AddParticipantComment.graphql';
import useCase from '@platform-ui/case/hooks/useCase.js';
import { FormEvent } from 'react';
import PanelWrapper from './PanelWrapper.js';

const MAX_CHARACTERS = 600;
const SendMessagePanel = () => {
  const {
    case_: { id },
    refetch,
  } = useCase();
  const { showSnackbar } = useSnackbar();
  const [addParticipantComment, { loading }] = useMutation<
    { case: { addParticipantComment: MutationResponse<Case> } },
    { input: AddCommentInput }
  >(AddParticipantComment);

  const {
    dispatch,
    formData,
    errors,
    trySubmit,
    isPristine,
    resetForm,
    counters: { missingRequiredFields },
  } = useForm(
    {
      message: '',
    },
    {
      message: requiredField(({ message }) =>
        characterCountValidation(MAX_CHARACTERS, 'message', message),
      ),
    },
    ValidationMode.OnSubmit,
  );

  const { message } = formData;

  const resetFields = () => {
    dispatch(fieldUpdate('message', ''));
    resetForm();
  };

  const doMutation = async (e?: FormEvent<HTMLFormElement>): Promise<void> => {
    e?.preventDefault();
    dispatch(fieldUpdate('message', message.trim()));
    trySubmit(async () => {
      if (isPristine) return;
      let error = 'Something went wrong';
      try {
        const {
          data: {
            case: {
              addParticipantComment: { metadata },
            },
          },
        } = await addParticipantComment({
          variables: {
            input: {
              caseId: id,
              content: message.trim(),
            },
          },
        });
        if (metadata && metadata.status < 400) {
          resetFields();
          refetch();
          showSnackbar('Successfully sent message.');
          return;
        }

        error = TransactionErrorDisplay[metadata.errors[0]] || metadata.errors[0];
        showSnackbar(
          error,
          <Button variant="text" textColor={Color.Link} onClick={(): Promise<void> => doMutation()}>
            Try Again
          </Button>,
        );
      } catch (e) {
        showSnackbar(
          error,
          <Button variant="text" textColor={Color.Link} onClick={(): Promise<void> => doMutation()}>
            Try Again
          </Button>,
        );
      }
    });
  };

  const handleDragStart = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
  };

  return (
    <PanelWrapper>
      <form
        className="w-full flex-grow flex flex-col gap-6"
        onSubmit={doMutation}
        noValidate={true}
      >
        <TextInput
          id="message"
          label="Enter new message here..."
          onChange={(value): void => dispatch(fieldUpdate('message', value))}
          onDragStart={handleDragStart}
          onDrop={handleDrop}
          value={message}
          error={errors.message as string}
          type="text"
          required
          multiline={true}
          rows={3}
          characterCounter={{ max: MAX_CHARACTERS }}
        />
        <div className="flex flex-col gap-4">
          <ErrorDisplay errors={missingRequiredFields} visible={missingRequiredFields > 0} />
          <div className="flex gap-6">
            <Button type="submit" disabled={loading} variant="text">
              Send Message
            </Button>
            <Button variant="text" onClick={resetFields}>
              Cancel
            </Button>
          </div>
        </div>
      </form>
    </PanelWrapper>
  );
};

export default SendMessagePanel;
